import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AssignManufacturerForm } from 'bom/components';
import { SolveProblemsStep } from 'bom/enums';
import { useBomImportActions } from 'bom/hooks';
import { useBomPartsImportContext } from 'bom/states';
import { IAssignManufacturerForm, IImportNotValidPartsFilters } from 'bom/types';
import { getPossibleManufacturerOptions, mapUpsertImportPartsData } from 'bom/utils';
import { AgGridTable } from 'core/components';
import { useAgGridClientTableContext } from 'core/states';
import { Color } from 'theme/enums';

interface IProps {
  filters: IImportNotValidPartsFilters;
  stepType: SolveProblemsStep;
}

const ImportPartTable = ({ filters, stepType }: IProps) => {
  const [closeSelection, setCloseSelection] = useState(false);
  const [isOpenAssignManufacturer, setIsOpenAssignManufacturer] = useState(false);

  const { t } = useTranslation();
  const { setProblems, isAlternateVersionEnabled } = useBomPartsImportContext();
  const { gridApi, rows, selectedRows, updateRows } = useAgGridClientTableContext();

  const { upsertImportParts } = useBomImportActions();

  const commonManufacturerOptions =
    selectedRows.length > 0 && SolveProblemsStep.MULTIPLE_MANUFACTURERS === stepType
      ? selectedRows
          .map((row) => row.possibleManufacturers)
          .reduce((acc, options) => {
            return acc.filter((option) =>
              options.some((opt) => opt.manufacturerId === option.manufacturerId)
            );
          })
      : [];

  const selectionActions = () => [
    {
      label: t('buttonLabelAssignManufacturer'),
      color: Color.PRIMARY,
      onClick: () => setIsOpenAssignManufacturer(true),
      disabled:
        SolveProblemsStep.MULTIPLE_MANUFACTURERS === stepType &&
        commonManufacturerOptions.length === 0,
    },
    {
      label: t('buttonLabelOmitSelected'),
      color: Color.PRIMARY,
      onClick: () => onValueChanged('omit', true),
    },
    {
      label: t('buttonLabelDoNotAssignManufacturer'),
      color: Color.PRIMARY,
      onClick: () => onValueChanged('doNotAssignManufacturer'),
    },
  ];

  const onAssignManufacturerSubmit = async ({ manufacturer }: IAssignManufacturerForm) => {
    onValueChanged('manufacturer', manufacturer);

    handleOnClose();
  };

  const onValueChanged = async (field: string, newValue = null) => {
    gridApi?.setGridOption('loading', true);

    const { success } = await upsertImportParts(
      mapUpsertImportPartsData(
        selectedRows,
        field,
        newValue,
        selectedRows.length === rows.length,
        filters
      )
    );

    if (!success) {
      gridApi?.setGridOption('loading', false);

      return;
    }

    updateRows({ remove: selectedRows });

    setProblems((prev) => ({
      ...prev,
      [stepType]: {
        solved: prev[stepType]?.solved + selectedRows?.length,
        unsolved: prev[stepType]?.unsolved - selectedRows?.length,
      },
    }));

    gridApi?.setGridOption('loading', false);

    return true;
  };

  const handleOnClose = () => {
    setIsOpenAssignManufacturer(false);
    setCloseSelection(true);
  };

  return (
    <>
      <AgGridTable
        selectionActions={selectionActions}
        closeSelection={closeSelection}
        setCloseSelection={setCloseSelection}
      />

      <AssignManufacturerForm
        isOpen={isOpenAssignManufacturer}
        onClose={handleOnClose}
        onSubmit={onAssignManufacturerSubmit}
        options={getPossibleManufacturerOptions(commonManufacturerOptions)}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />
    </>
  );
};

export default ImportPartTable;
